import { inject, Injectable, signal } from '@angular/core';

import { AuthService } from '@core/auth/services/auth.service';
import { ProfilesService } from '@core/services/profiles.service';
import { ThemeService } from '@core/services/theme.service';

import { ContentCacheService } from './content-cache.service';
import { ContentPrefetchService } from './content-prefetch.service';
import { LanguageService } from './language.service';
import { OrganizationService } from './organization.service';
import { PatientService } from './patient.service';
import { UpdateService } from './update.service';

@Injectable({ providedIn: 'root' })
export class InitService {
  private readonly authService = inject(AuthService);
  private readonly contentCacheService = inject(ContentCacheService);
  private readonly contentPrefetchService = inject(ContentPrefetchService);
  private readonly languageService = inject(LanguageService);
  private readonly organizationService = inject(OrganizationService);
  private readonly patientService = inject(PatientService);
  private readonly profilesService = inject(ProfilesService);
  private readonly themeService = inject(ThemeService);
  private readonly updateService = inject(UpdateService);

  private readonly _isInitialized = signal(false);
  readonly isInitialized = this._isInitialized.asReadonly();

  async initApp(): Promise<void> {
    this.languageService.setDefaultLanguage();

    try {
      const [, isAuthenticated] = await Promise.all([
        this.contentCacheService.hydrateFromStorage(),
        this.authService.checkAuthentication(),
      ]);
      // Fetch organization first (other calls depend on organizationId)
      await this.organizationService.fetchOrganization();
      // Always fetch public theme first (no auth required, ensures theme is available immediately)
      await this.themeService.fetchPublicTheme();
      if (isAuthenticated) {
        await this.fetchDataForAuthUser().catch(console.error);
        this.contentPrefetchService.startAutoRefresh(() => this.languageService.currentLanguage());
      }
      this.updateService.checkForUpdates().catch(console.error);
    } catch (error) {
      console.error('App initialization failed:', error);
    } finally {
      this._isInitialized.set(true);
    }
  }

  private async fetchDataForAuthUser(): Promise<void> {
    const token = await this.authService.getTokenPayload();
    if (token.patientId && token.trusted?.v) {
      // fetchPatientData() handles language + theme in correct order
      await Promise.all([this.patientService.fetchPatientData(), this.profilesService.loadProfiles()]);
    }
  }
}
